import type { PatternRenderContext, SnakeGameState } from './types'

function parseColor(colorStr: string): [number, number, number] {
  const match = colorStr?.match(/\d+/g)
  return match ? [Number(match[0]), Number(match[1]), Number(match[2])] : [255, 255, 255]
}

function fillBlock(
  data: Uint8ClampedArray,
  width: number,
  x: number,
  y: number,
  step: number,
  maxX: number,
  maxY: number,
  r: number,
  g: number,
  b: number
) {
  const bh = Math.min(step, maxY - y)
  const bw = Math.min(step, maxX - x)
  for (let blockY = 0; blockY < bh; blockY++) {
    const rowIndex = ((y + blockY) * width + x) * 4
    for (let blockX = 0; blockX < bw; blockX++) {
      const index = rowIndex + blockX * 4
      data[index] = r
      data[index + 1] = g
      data[index + 2] = b
      data[index + 3] = 255
    }
  }
}

export function renderGradient({ ctx, width, height, colors }: PatternRenderContext) {
  const gradient = ctx.createLinearGradient(0, 0, width, height)
  gradient.addColorStop(0, colors[0])
  gradient.addColorStop(0.5, colors[1])
  gradient.addColorStop(1, colors[2])
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, width, height)
}

export function renderGradient4({ ctx, width, height, colors }: PatternRenderContext) {
  const imageData = ctx.createImageData(width, height)
  const data = imageData.data

  const [r1, g1, b1] = parseColor(colors[0])
  const [r2, g2, b2] = parseColor(colors[1])
  const [r3, g3, b3] = parseColor(colors[2])
  const [r4, g4, b4] = parseColor(colors[3])

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const xRatio = x / width
      const yRatio = y / height

      const topR = r1 + (r2 - r1) * xRatio
      const topG = g1 + (g2 - g1) * xRatio
      const topB = b1 + (b2 - b1) * xRatio

      const bottomR = r3 + (r4 - r3) * xRatio
      const bottomG = g3 + (g4 - g3) * xRatio
      const bottomB = b3 + (b4 - b3) * xRatio

      const finalR = topR + (bottomR - topR) * yRatio
      const finalG = topG + (bottomG - topG) * yRatio
      const finalB = topB + (bottomB - topB) * yRatio

      const index = (y * width + x) * 4
      data[index] = Math.floor(finalR)
      data[index + 1] = Math.floor(finalG)
      data[index + 2] = Math.floor(finalB)
      data[index + 3] = 255
    }
  }
  ctx.putImageData(imageData, 0, 0)
}

export function renderSolid({ ctx, width, height, colors }: PatternRenderContext) {
  ctx.fillStyle = colors[0]
  ctx.fillRect(0, 0, width, height)
}

export function renderCheckerboard({ ctx, width, height, gridSize, colors }: PatternRenderContext) {
  const cellSize = gridSize
  for (let x = 0; x < width; x += cellSize) {
    for (let y = 0; y < height; y += cellSize) {
      const isEven = (Math.floor(x / cellSize) + Math.floor(y / cellSize)) % 2 === 0
      ctx.fillStyle = isEven ? colors[0] : colors[1]
      ctx.fillRect(x, y, cellSize, cellSize)
    }
  }
}

export function renderRainbow({ ctx, width, height, time }: PatternRenderContext) {
  const imageData = ctx.createImageData(width, height)
  const data = imageData.data

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const wave = Math.sin((x + time * 0.1) * 0.01) + Math.sin((y + time * 0.1) * 0.01)
      const hue = (wave + time * 0.001) % (2 * Math.PI)

      const r = Math.sin(hue) * 127 + 128
      const g = Math.sin(hue + 2.094) * 127 + 128
      const b = Math.sin(hue + 4.188) * 127 + 128

      const index = (y * width + x) * 4
      data[index] = Math.floor(r)
      data[index + 1] = Math.floor(g)
      data[index + 2] = Math.floor(b)
      data[index + 3] = 255
    }
  }
  ctx.putImageData(imageData, 0, 0)
}

export function renderColorBars({ ctx, width, height, time }: PatternRenderContext) {
  const barWidth = width / 7
  const barColors = ["#ff0000", "#ff8000", "#ffff00", "#00ff00", "#0080ff", "#0000ff", "#8000ff"]

  barColors.forEach((color, i) => {
    const alpha = Math.sin(time * 0.002 + i) * 0.3 + 0.7
    ctx.fillStyle = color
    ctx.globalAlpha = alpha
    ctx.fillRect(i * barWidth, 0, barWidth, height)
  })
  ctx.globalAlpha = 1
}

export function renderPulse(
  { ctx, width, height, time, colors }: PatternRenderContext,
  distanceMapCache: { width: number; height: number; map: Float32Array } | null,
  setDistanceMapCache: (cache: { width: number; height: number; map: Float32Array }) => void
) {
  const centerX = width / 2
  const centerY = height / 2
  const maxRadius = Math.sqrt(centerX * centerX + centerY * centerY)

  const colorPalette = colors.map(color => parseColor(color))
  if (colorPalette.length === 0) {
    colorPalette.push([255, 255, 255])
  }

  const timeOffset = time * 0.002

  const offscreenCanvas = document.createElement('canvas')
  offscreenCanvas.width = width
  offscreenCanvas.height = height
  const offscreenCtx = offscreenCanvas.getContext('2d')

  if (offscreenCtx) {
    offscreenCtx.clearRect(0, 0, width, height)

    const baseColor = colorPalette[0]
    offscreenCtx.fillStyle = `rgba(${baseColor[0] * 0.2}, ${baseColor[1] * 0.2}, ${baseColor[2] * 0.2}, 0.3)`
    offscreenCtx.fillRect(0, 0, width, height)

    const numRings = 12

    for (let i = 0; i < numRings; i++) {
      const ringPhase = (i / numRings) * Math.PI * 2
      const ringProgress = (Math.sin(timeOffset * 0.7 + ringPhase) + 1) * 0.5
      const ringRadius = maxRadius * ringProgress * 0.9

      const colorIndex = i % colorPalette.length
      const [r, g, b] = colorPalette[colorIndex]

      const gradient = offscreenCtx.createRadialGradient(
        centerX, centerY, Math.max(0, ringRadius - maxRadius * 0.15),
        centerX, centerY, ringRadius + maxRadius * 0.15
      )

      const intensity = Math.sin(timeOffset * 0.3 + ringPhase) * 0.3 + 0.7
      const opacity = 0.4 + (i / numRings) * 0.2
      const ringColor = `rgba(${r * intensity}, ${g * intensity}, ${b * intensity}, ${opacity})`

      gradient.addColorStop(0, 'rgba(0, 0, 0, 0)')
      gradient.addColorStop(0.5, `rgba(${r * intensity * 0.7}, ${g * intensity * 0.7}, ${b * intensity * 0.7}, ${opacity * 0.5})`)
      gradient.addColorStop(0.8, ringColor)
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)')

      offscreenCtx.fillStyle = gradient
      offscreenCtx.beginPath()
      offscreenCtx.arc(centerX, centerY, maxRadius, 0, 2 * Math.PI)
      offscreenCtx.fill()
    }

    for (let i = 0; i < colorPalette.length; i++) {
      const [r, g, b] = colorPalette[i]
      const phaseOffset = (i / colorPalette.length) * Math.PI * 2

      const glowSize = (Math.sin(timeOffset * 0.4 + phaseOffset) * 0.2 + 0.8) * maxRadius * 0.6

      const centerGlow = offscreenCtx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, glowSize
      )

      const glowIntensity = Math.sin(timeOffset * 0.3 + phaseOffset) * 0.3 + 0.7
      const glowColor = `rgba(${r * glowIntensity}, ${g * glowIntensity}, ${b * glowIntensity}, 0.6)`

      centerGlow.addColorStop(0, glowColor)
      centerGlow.addColorStop(0.7, `rgba(${r * glowIntensity * 0.5}, ${g * glowIntensity * 0.5}, ${b * glowIntensity * 0.5}, 0.3)`)
      centerGlow.addColorStop(1, 'rgba(0, 0, 0, 0)')

      offscreenCtx.globalCompositeOperation = 'lighter'
      offscreenCtx.fillStyle = centerGlow
      offscreenCtx.beginPath()
      offscreenCtx.arc(centerX, centerY, glowSize, 0, 2 * Math.PI)
      offscreenCtx.fill()
    }

    offscreenCtx.globalCompositeOperation = 'lighter'
    const numRays = 8
    for (let i = 0; i < numRays; i++) {
      const angle = (i / numRays) * Math.PI * 2
      const rayLength = maxRadius * (Math.sin(timeOffset * 0.2 + i) * 0.2 + 0.8)

      const [r, g, b] = colorPalette[i % colorPalette.length]

      const rayGradient = offscreenCtx.createLinearGradient(
        centerX, centerY,
        centerX + Math.cos(angle) * rayLength,
        centerY + Math.sin(angle) * rayLength
      )

      rayGradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.7)`)
      rayGradient.addColorStop(1, 'rgba(0, 0, 0, 0)')

      offscreenCtx.save()
      offscreenCtx.beginPath()
      offscreenCtx.moveTo(centerX, centerY)
      offscreenCtx.lineTo(
        centerX + Math.cos(angle - 0.1) * rayLength * 0.2,
        centerY + Math.sin(angle - 0.1) * rayLength * 0.2
      )
      offscreenCtx.lineTo(
        centerX + Math.cos(angle) * rayLength,
        centerY + Math.sin(angle) * rayLength
      )
      offscreenCtx.lineTo(
        centerX + Math.cos(angle + 0.1) * rayLength * 0.2,
        centerY + Math.sin(angle + 0.1) * rayLength * 0.2
      )
      offscreenCtx.closePath()

      offscreenCtx.fillStyle = rayGradient
      offscreenCtx.fill()
      offscreenCtx.restore()
    }

    const pulseIntensity = Math.sin(timeOffset * 0.8) * 0.15 + 0.85
    offscreenCtx.globalAlpha = pulseIntensity

    ctx.drawImage(offscreenCanvas, 0, 0)
  } else {
    // Fallback
    const imageData = ctx.createImageData(width, height)
    const data = imageData.data

    const baseStep = 3
    const minStep = 1

    let distanceMapData: Float32Array

    if (!distanceMapCache || distanceMapCache.width !== width || distanceMapCache.height !== height) {
      const newCache = {
        width,
        height,
        map: new Float32Array(width * height)
      }

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const dx = x - centerX
          const dy = y - centerY
          const distance = Math.sqrt(dx * dx + dy * dy) / maxRadius
          newCache.map[y * width + x] = distance
        }
      }

      setDistanceMapCache(newCache)
      distanceMapData = newCache.map
    } else {
      distanceMapData = distanceMapCache.map
    }

    const pulseMap = new Float32Array(width * height)

    for (let i = 0; i < distanceMapData.length; i++) {
      const distance = distanceMapData[i]
      const pulse1 = Math.sin(distance * 8 - timeOffset) * 0.5 + 0.5
      const pulse2 = Math.sin(distance * 12 - timeOffset * 1.3) * 0.3 + 0.7
      pulseMap[i] = (pulse1 + pulse2) * 0.5
    }

    for (let y = 0; y < height; y++) {
      const yDist = Math.abs(y - centerY) / centerY
      const yStep = Math.max(minStep, Math.floor(baseStep * yDist + 0.5))

      for (let x = 0; x < width; x += yStep) {
        const xDist = Math.abs(x - centerX) / centerX
        const step = Math.max(minStep, Math.floor(baseStep * (xDist + yDist) / 2 + 0.5))

        const pulse = pulseMap[y * width + x]

        const distance = distanceMapData[y * width + x]
        const colorIdx = Math.floor(distance * colorPalette.length) % colorPalette.length
        const [r, g, b] = colorPalette[colorIdx]

        for (let dy = 0; dy < step && y + dy < height; dy++) {
          for (let dx = 0; dx < step && x + dx < width; dx++) {
            const index = ((y + dy) * width + (x + dx)) * 4
            data[index] = Math.floor(r * pulse)
            data[index + 1] = Math.floor(g * pulse)
            data[index + 2] = Math.floor(b * pulse)
            data[index + 3] = 255
          }
        }
      }
    }

    ctx.putImageData(imageData, 0, 0)
  }
}

export function renderSpiral({ ctx, width, height, time }: PatternRenderContext) {
  const centerX = width / 2
  const centerY = height / 2
  const imageData = ctx.createImageData(width, height)
  const data = imageData.data

  const totalPixels = width * height
  const step = totalPixels > 2000000 ? 4 : totalPixels > 500000 ? 3 : 2

  const timeOffset = time * 0.002
  const spiralTightness = 0.01
  const invTwoPi = 1 / (2 * Math.PI)

  const hslToRgb = (h: number, s: number = 0.8, l: number = 0.5): [number, number, number] => {
    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l - c / 2

    let r: number, g: number, b: number
    if (h < 60) {
      [r, g, b] = [c, x, 0]
    } else if (h < 120) {
      [r, g, b] = [x, c, 0]
    } else if (h < 180) {
      [r, g, b] = [0, c, x]
    } else if (h < 240) {
      [r, g, b] = [0, x, c]
    } else if (h < 300) {
      [r, g, b] = [x, 0, c]
    } else {
      [r, g, b] = [c, 0, x]
    }

    return [
      ((r + m) * 255) | 0,
      ((g + m) * 255) | 0,
      ((b + m) * 255) | 0
    ]
  }

  for (let y = 0; y < height; y += step) {
    const dy = y - centerY
    const dySquared = dy * dy

    for (let x = 0; x < width; x += step) {
      const dx = x - centerX
      const distance = Math.sqrt(dx * dx + dySquared)

      const spiral = (Math.atan2(dy, dx) + distance * spiralTightness + timeOffset) % (2 * Math.PI)
      const hue = spiral * invTwoPi * 360
      const [r, g, b] = hslToRgb(hue)

      const intensity = Math.sin(spiral * 2) * 0.3 + 0.7
      const finalR = (r * intensity) | 0
      const finalG = (g * intensity) | 0
      const finalB = (b * intensity) | 0

      const maxDy = Math.min(step, height - y)
      const maxDx = Math.min(step, width - x)

      for (let blockY = 0; blockY < maxDy; blockY++) {
        const rowIndex = ((y + blockY) * width + x) * 4
        for (let blockX = 0; blockX < maxDx; blockX++) {
          const index = rowIndex + blockX * 4
          data[index] = finalR
          data[index + 1] = finalG
          data[index + 2] = finalB
          data[index + 3] = 255
        }
      }
    }
  }
  ctx.putImageData(imageData, 0, 0)
}

export function renderOrangeTeal({ ctx, width, height, time }: PatternRenderContext) {
  const imageData = ctx.createImageData(width, height)
  const data = imageData.data
  const totalPixels = width * height
  const step = totalPixels > 2000000 ? 4 : totalPixels > 500000 ? 3 : 2

  const timeOffset = time * 0.002
  const invWidth = 1 / width

  for (let y = 0; y < height; y += step) {
    const yVariation = Math.sin(timeOffset + y * 0.01) * 30

    for (let x = 0; x < width; x += step) {
      const sweepPosition = (Math.sin(timeOffset) + 1) * 0.5
      const normalizedX = x * invWidth
      const distanceFromSweep = Math.abs(normalizedX - sweepPosition)

      let r: number, g: number, b: number

      if (distanceFromSweep < 0.3) {
        const mixRatio = (Math.cos(distanceFromSweep * 10.47) + 1) * 0.5
        r = 255 * mixRatio
        g = 140 * mixRatio + 128 * (1 - mixRatio)
        b = 128 * (1 - mixRatio)
      } else if (normalizedX < sweepPosition) {
        r = 0
        g = (128 + yVariation) | 0
        b = (128 + Math.cos(timeOffset + y * 0.01) * 30) | 0
      } else {
        r = 255
        g = (140 + yVariation * 0.8) | 0
        b = (Math.sin(time * 0.005 + y * 0.01) * 20) | 0
      }

      r = Math.max(0, Math.min(255, r)) | 0
      g = Math.max(0, Math.min(255, g)) | 0
      b = Math.max(0, Math.min(255, b)) | 0

      fillBlock(data, width, x, y, step, width, height, r, g, b)
    }
  }
  ctx.putImageData(imageData, 0, 0)
}

export function renderMagentaSummer({ ctx, width, height, time }: PatternRenderContext) {
  const imageData = ctx.createImageData(width, height)
  const data = imageData.data
  const totalPixels = width * height
  const step = totalPixels > 2000000 ? 4 : totalPixels > 500000 ? 3 : 2

  const centerX = width / 2
  const centerY = height / 2
  const maxDistance = Math.sqrt(centerX * centerX + centerY * centerY)
  const invMaxDistance = 1 / maxDistance
  const timeOffset1 = time * 0.003
  const timeOffset2 = time * 0.002

  for (let y = 0; y < height; y += step) {
    const dy = y - centerY
    const dySquared = dy * dy

    for (let x = 0; x < width; x += step) {
      const dx = x - centerX
      const distance = Math.sqrt(dx * dx + dySquared)
      const normalizedDistance = distance * invMaxDistance

      const mainPulse = Math.sin(timeOffset1 - normalizedDistance * 8) * 0.5 + 0.5
      const mixPulse = Math.sin(timeOffset2 + normalizedDistance * 6) * 0.5 + 0.5

      let r: number, g: number, b: number

      if (mixPulse > 0.5) {
        r = 255
        g = (165 * mainPulse) | 0
        b = (255 * (1 - mainPulse)) | 0
      } else {
        r = (255 * (1 - mainPulse)) | 0
        g = (128 * mainPulse + 20 * (1 - mainPulse)) | 0
        b = (128 * mainPulse + 147 * (1 - mainPulse)) | 0
      }

      r = Math.max(0, Math.min(255, r + Math.sin(time * 0.004 + x * 0.01 + y * 0.01) * 20)) | 0
      g = Math.max(0, Math.min(255, g + Math.cos(time * 0.004 + x * 0.01) * 15)) | 0
      b = Math.max(0, Math.min(255, b + Math.sin(time * 0.004 + y * 0.01) * 25)) | 0

      fillBlock(data, width, x, y, step, width, height, r, g, b)
    }
  }
  ctx.putImageData(imageData, 0, 0)
}

export function renderSummer({ ctx, width, height, time }: PatternRenderContext) {
  const imageData = ctx.createImageData(width, height)
  const data = imageData.data
  const totalPixels = width * height
  const step = totalPixels > 2000000 ? 4 : totalPixels > 500000 ? 3 : 2

  const timeOffset1 = time * 0.003
  const timeOffset2 = time * 0.002
  const timeOffset3 = time * 0.004

  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const wave1 = Math.sin(x * 0.008 + timeOffset1) * 0.5 + 0.5
      const wave2 = Math.sin(y * 0.006 + timeOffset2) * 0.5 + 0.5
      const wave3 = Math.sin((x + y) * 0.004 + timeOffset3) * 0.5 + 0.5

      const heatIntensity = (wave1 + wave2 + wave3) / 3

      let r: number, g: number, b: number

      if (heatIntensity < 0.25) {
        const ratio = heatIntensity * 4
        r = 255
        g = (215 * (1 - ratio) + 165 * ratio) | 0
        b = 0
      } else if (heatIntensity < 0.5) {
        const ratio = (heatIntensity - 0.25) * 4
        r = 255
        g = (165 * (1 - ratio) + 255 * ratio) | 0
        b = 0
      } else if (heatIntensity < 0.75) {
        const ratio = (heatIntensity - 0.5) * 4
        r = 255
        g = (255 * (1 - ratio) + 69 * ratio) | 0
        b = 0
      } else {
        const ratio = (heatIntensity - 0.75) * 4
        r = 255
        g = (69 * (1 - ratio) + 215 * ratio) | 0
        b = 0
      }

      fillBlock(data, width, x, y, step, width, height, r, g, b)
    }
  }
  ctx.putImageData(imageData, 0, 0)
}

export function renderBluePurple({ ctx, width, height, time }: PatternRenderContext) {
  const imageData = ctx.createImageData(width, height)
  const data = imageData.data
  const totalPixels = width * height
  const step = totalPixels > 2000000 ? 4 : totalPixels > 500000 ? 3 : 2

  const centerX = width / 2
  const centerY = height / 2
  const timeOffset = time * 0.002

  for (let y = 0; y < height; y += step) {
    const dy = y - centerY
    const dySquared = dy * dy

    for (let x = 0; x < width; x += step) {
      const dx = x - centerX
      const distance = Math.sqrt(dx * dx + dySquared)

      const spiral = Math.atan2(dy, dx) + distance * 0.008 + timeOffset
      const spiralIntensity = Math.sin(spiral * 3) * 0.5 + 0.5
      const radialPulse = Math.sin(distance * 0.01 - time * 0.003) * 0.5 + 0.5
      const combinedIntensity = (spiralIntensity + radialPulse) * 0.5

      let r: number, g: number, b: number

      if (combinedIntensity < 0.33) {
        const ratio = combinedIntensity * 3
        r = (65 * ratio) | 0
        g = (105 * ratio) | 0
        b = (139 + (225 - 139) * ratio) | 0
      } else if (combinedIntensity < 0.66) {
        const ratio = (combinedIntensity - 0.33) * 3
        r = (65 * (1 - ratio) + 138 * ratio) | 0
        g = (105 * (1 - ratio) + 43 * ratio) | 0
        b = (225 * (1 - ratio) + 226 * ratio) | 0
      } else {
        const ratio = (combinedIntensity - 0.66) * 3
        r = (138 * (1 - ratio) + 148 * ratio) | 0
        g = (43 * (1 - ratio)) | 0
        b = (226 * (1 - ratio) + 211 * ratio) | 0
      }

      const starNoise = Math.sin(x * 0.1) * Math.sin(y * 0.1) * Math.sin(time * 0.01)
      if (starNoise > 0.95) {
        r = Math.min(255, r + 100)
        g = Math.min(255, g + 100)
        b = Math.min(255, b + 100)
      }

      fillBlock(data, width, x, y, step, width, height, r, g, b)
    }
  }
  ctx.putImageData(imageData, 0, 0)
}

export function renderSnake(
  { ctx, width, height, time, gridSize }: PatternRenderContext,
  snakeGameState: SnakeGameState | null
) {
  ctx.fillStyle = '#000000'
  ctx.fillRect(0, 0, width, height)

  if (!snakeGameState) return

  snakeGameState.snake.forEach((segment, index) => {
    const x = segment.x * gridSize
    const y = segment.y * gridSize

    if (index === 0) {
      ctx.fillStyle = '#00ff88'
      ctx.fillRect(x, y, gridSize - 2, gridSize - 2)

      ctx.fillStyle = '#000'
      const eyeSize = Math.max(3, gridSize / 8)
      const cX = x + gridSize / 2
      const cY = y + gridSize / 2
      const eyeOffset = gridSize / 6

      let eye1X: number, eye1Y: number, eye2X: number, eye2Y: number

      switch (snakeGameState.direction) {
        case 'UP':
          eye1X = cX - eyeOffset; eye1Y = cY - eyeOffset
          eye2X = cX + eyeOffset; eye2Y = cY - eyeOffset
          break
        case 'DOWN':
          eye1X = cX - eyeOffset; eye1Y = cY + eyeOffset
          eye2X = cX + eyeOffset; eye2Y = cY + eyeOffset
          break
        case 'LEFT':
          eye1X = cX - eyeOffset; eye1Y = cY - eyeOffset
          eye2X = cX - eyeOffset; eye2Y = cY + eyeOffset
          break
        case 'RIGHT':
        default:
          eye1X = cX + eyeOffset; eye1Y = cY - eyeOffset
          eye2X = cX + eyeOffset; eye2Y = cY + eyeOffset
          break
      }

      ctx.fillRect(eye1X - eyeSize / 2, eye1Y - eyeSize / 2, eyeSize, eyeSize)
      ctx.fillRect(eye2X - eyeSize / 2, eye2Y - eyeSize / 2, eyeSize, eyeSize)
    } else {
      const intensity = 1 - (index / snakeGameState.snake.length) * 0.6
      const green = Math.floor(255 * intensity)
      const blue = Math.floor(136 * intensity)
      ctx.fillStyle = `rgb(0, ${green}, ${blue})`
      ctx.fillRect(x + 1, y + 1, gridSize - 4, gridSize - 4)
    }
  })

  if (snakeGameState.visitedCells && snakeGameState.isPlaying && !snakeGameState.isGameOver && !snakeGameState.isVictory) {
    const fontSize = Math.max(12, Math.min(gridSize / 4, 48))
    ctx.font = `bold ${fontSize}px Arial, sans-serif`
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillStyle = "rgba(255, 255, 255, 1)"
    ctx.shadowColor = "rgba(0, 0, 0, 0.8)"
    ctx.shadowBlur = 2
    ctx.shadowOffsetX = 1
    ctx.shadowOffsetY = 1

    const panelsX = Math.ceil(width / gridSize)
    const panelsY = Math.ceil(height / gridSize)

    for (let row = 0; row < panelsY; row++) {
      for (let col = 0; col < panelsX; col++) {
        const cellKey = `${col},${row}`
        if (snakeGameState.visitedCells.has(cellKey)) {
          const panelNumber = `${row + 1}.${col + 1}`
          const cX = col * gridSize + gridSize / 2
          const cY = row * gridSize + gridSize / 2
          ctx.fillText(panelNumber, cX, cY)
        }
      }
    }

    ctx.shadowColor = "transparent"
    ctx.shadowBlur = 0
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
  }

  const foodX = snakeGameState.food.x * gridSize
  const foodY = snakeGameState.food.y * gridSize
  const pulseIntensity = Math.sin(time * 0.008) * 0.3 + 0.7
  const foodSize = (gridSize - 4) * pulseIntensity
  const offset = (gridSize - foodSize) / 2

  ctx.fillStyle = `rgb(255, ${Math.floor(100 * pulseIntensity)}, 0)`
  ctx.fillRect(foodX + offset, foodY + offset, foodSize, foodSize)

  const gradient = ctx.createRadialGradient(
    foodX + gridSize / 2, foodY + gridSize / 2, 0,
    foodX + gridSize / 2, foodY + gridSize / 2, gridSize * 1.5
  )
  gradient.addColorStop(0, `rgba(255, 100, 0, ${pulseIntensity * 0.4})`)
  gradient.addColorStop(1, 'rgba(255, 100, 0, 0)')
  ctx.fillStyle = gradient
  ctx.fillRect(foodX - gridSize / 2, foodY - gridSize / 2, gridSize * 2, gridSize * 2)

  if (snakeGameState.score > 0) {
    ctx.fillStyle = '#ffffff'
    ctx.font = `bold ${Math.max(24, gridSize / 4)}px Arial`
    ctx.textAlign = 'left'
    ctx.fillText(`Score: ${snakeGameState.score}`, 20, 40)
  }

  if (snakeGameState.isGameOver) {
    ctx.fillStyle = 'rgba(255, 0, 0, 0.8)'
    ctx.fillRect(0, 0, width, height)

    ctx.fillStyle = '#ffffff'
    ctx.font = `bold ${Math.max(32, gridSize / 3)}px Arial`
    ctx.textAlign = 'center'
    ctx.fillText('GAME OVER', width / 2, height / 2)
    ctx.fillText(`Score Final: ${snakeGameState.score}`, width / 2, height / 2 + 50)
    ctx.fillText('Appuyez sur Espace pour rejouer', width / 2, height / 2 + 100)
  }

  if (snakeGameState.isVictory) {
    ctx.fillStyle = 'rgba(0, 0, 50, 0.9)'
    ctx.fillRect(0, 0, width, height)

    for (let i = 0; i < 100; i++) {
      const x = Math.random() * width
      const y = Math.random() * height
      const size = Math.random() * 8 + 2
      const hue = (time * 0.01 + i * 3.6) % 360

      ctx.fillStyle = `hsla(${hue}, 100%, 60%, ${Math.sin(time * 0.003 + i) * 0.5 + 0.5})`
      ctx.beginPath()
      ctx.arc(x, y, size, 0, Math.PI * 2)
      ctx.fill()
    }

    const bannerHeight = height * 0.3
    const bannerY = height / 2 - bannerHeight / 2

    const bannerGradient = ctx.createLinearGradient(0, bannerY, 0, bannerY + bannerHeight)
    bannerGradient.addColorStop(0, 'rgba(30, 60, 120, 0.8)')
    bannerGradient.addColorStop(0.5, 'rgba(50, 100, 180, 0.9)')
    bannerGradient.addColorStop(1, 'rgba(30, 60, 120, 0.8)')

    ctx.fillStyle = bannerGradient
    ctx.fillRect(width * 0.1, bannerY, width * 0.8, bannerHeight)

    ctx.strokeStyle = `hsl(${time * 0.05 % 360}, 100%, 70%)`
    ctx.lineWidth = 4
    ctx.strokeRect(width * 0.1, bannerY, width * 0.8, bannerHeight)

    ctx.fillStyle = '#ffffff'
    ctx.textAlign = 'center'

    const pulseScale = Math.sin(time * 0.005) * 0.1 + 1.1
    ctx.font = `bold ${Math.max(48, gridSize / 2) * pulseScale}px Arial`
    ctx.fillText('VICTOIRE !', width / 2, bannerY + bannerHeight * 0.35)

    ctx.font = `bold ${Math.max(32, gridSize / 3)}px Arial`
    ctx.fillText(`Félicitations !`, width / 2, bannerY + bannerHeight * 0.6)

    ctx.font = `${Math.max(24, gridSize / 4)}px Arial`
    ctx.fillText(`Score Final: ${snakeGameState.score}`, width / 2, bannerY + bannerHeight * 0.8)

    ctx.font = `${Math.max(18, gridSize / 5)}px Arial`
    ctx.fillText('Appuyez sur Espace pour rejouer', width / 2, height * 0.7)
  }

  if (!snakeGameState.isPlaying && !snakeGameState.isGameOver && !snakeGameState.isVictory) {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
    ctx.fillRect(0, 0, width, height)

    ctx.fillStyle = '#ffffff'
    ctx.font = `bold ${Math.max(28, gridSize / 4)}px Arial`
    ctx.textAlign = 'center'
    ctx.fillText('🐍 SNAKE GAME', width / 2, height / 2 - 60)
    ctx.font = `${Math.max(18, gridSize / 6)}px Arial`
    ctx.fillText('Flèches ou ZQSD pour jouer', width / 2, height / 2 - 10)
    ctx.fillText('Appuyez sur Espace pour démarrer', width / 2, height / 2 + 20)
  }
}
